/**
 * workbench-domain.js
 * Helpers for Workbench tool normalization and payload construction.
 */

const TOOL_LABELS = {
  "feature-to-stories": "Features → Initial Stories",
  "pi-slotting": "PI Slotting",
  "story-splitting": "Story Splitting",
  "acceptance-criteria": "Acceptance Criteria Generator",
  "backlog-refiner": "Backlog Refiner",
  "dod-ready-checker": "Definition of Done / Ready Checker",
  "experiment-designer": "Experiment Designer",
  "retro-topic-generator": "Retro Topic Generator",
  "risk-dependency-mapper": "Risk / Dependency Mapper",
  "story-mapping": "Story Mapping Assistant",
};

export function normalizeToolKey(toolKey) {
  if (!toolKey) {
    return "";
  }
  const key = String(toolKey).trim().toLowerCase();
  if (key === "splitter" || key === "story-splitter") {
    return "story-splitting";
  }
  if (key === "pi-slotter") {
    return "pi-slotting";
  }
  return key;
}

export function getWorkbenchToolLabel(toolKey) {
  const normalized = normalizeToolKey(toolKey);
  return TOOL_LABELS[normalized] || (normalized ? normalized.replace(/-/g, " ") : "Workbench Tool");
}

export function buildWorkbenchPayload({ toolKey, formData = {}, customBuilder = null } = {}) {
  const normalized = normalizeToolKey(toolKey);

  if (typeof customBuilder === "function") {
    try {
      const custom = customBuilder();
      if (custom && typeof custom === "object") {
        return custom;
      }
    } catch (error) {
      // fall through to default handling
    }
  }

  switch (normalized) {
    case "feature-to-stories": {
      const features = toLineArray(formData.features || formData.featuresText);
      const granularity = sanitizeText(formData.granularity) || "lean";
      return {
        features,
        granularity,
      };
    }
    case "pi-slotting": {
      const ranked = toLineArray(formData.rankedFeatures || formData.rankedFeaturesText);
      return {
        features_ranked: ranked,
        capacity_per_iteration: toNumber(formData.capacity),
        iterations: toNumber(formData.iterations),
      };
    }
    case "story-splitting": {
      return {
        story: sanitizeText(formData.story),
        heuristic: sanitizeText(formData.heuristic) || "rules",
      };
    }
    case "acceptance-criteria": {
      return {
        story: sanitizeText(formData.story),
        includeGherkin: toBoolean(formData.includeGherkin),
      };
    }
    case "backlog-refiner": {
      return {
        backlogItems: toLineArray(formData.backlogItems || formData.backlogItemsText),
        considerDependencies: toBoolean(formData.considerDependencies),
      };
    }
    case "dod-ready-checker": {
      return {
        checkType: sanitizeText(formData.checkType) || "dod",
        definition: sanitizeText(formData.definition),
      };
    }
    case "experiment-designer": {
      return {
        hypothesis: sanitizeText(formData.hypothesis),
        metrics: {
          primary: sanitizeText(formData.primaryMetric),
          guardrail: sanitizeText(formData.guardrailMetric),
        },
        audience: sanitizeText(formData.audience),
      };
    }
    case "retro-topic-generator": {
      return {
        signals: sanitizeText(formData.signals),
        metrics: sanitizeText(formData.metrics),
      };
    }
    case "risk-dependency-mapper": {
      return {
        workItems: toLineArray(formData.workItems || formData.workItemsText),
        dependencies: parseDependencies(formData.dependencies || formData.dependenciesText),
        outputStyle: sanitizeText(formData.outputStyle) || "list",
      };
    }
    case "story-mapping": {
      return {
        epic: sanitizeText(formData.epic),
        description: sanitizeText(formData.description),
      };
    }
    default: {
      const story = sanitizeText(formData.story);
      const heuristic = sanitizeText(formData.heuristic);
      return { story, heuristic };
    }
  }
}

function toLineArray(value) {

  if (Array.isArray(value)) {

    return value.map((item) => sanitizeText(item)).filter(Boolean);

  }

  if (typeof value === "string") {

    return value

      .split(/\r?\n/)

      .map((line) => line.trim())

      .filter(Boolean);

  }

  return [];

}



function sanitizeText(value) {
  if (value == null) {
    return '';
  }
  return String(value).trim();
}

function toNumber(value) {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
}

function toBoolean(value) {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return ['true', '1', 'yes', 'on'].includes(normalized);
  }
  return Boolean(value);
}

function parseDependencies(value) {

  const lines = toLineArray(value);

  return lines.map((line) => {

    const parts = line.split(/->|\\u2192|=>/);

    if (parts.length >= 2) {

      return { from: parts[0].trim(), to: parts[1].trim() };

    }

    return { relation: line };

  });

}



export function buildWorkbenchPlaceholder({
  toolKey,
  payload,
  teamName = "Team",
} = {}) {
  const normalized = normalizeToolKey(toolKey);
  const title = "Workbench (mocked output)";
  const payloadText = typeof payload === "undefined" ? "{}" : JSON.stringify(payload, null, 2);

  return {
    title,
    sections: [
      [
        "Note",
        "The /api/workbench endpoint is not available. Showing a placeholder response with your input.",
      ],
      ["Tool", normalized || "unknown"],
      ["Team", teamName || "—"],
      ["Payload", payloadText],
    ],
  };
}

export { TOOL_LABELS };

if (typeof window !== "undefined") {
  window.AgileProDomain = window.AgileProDomain || {};
  window.AgileProDomain.workbench = window.AgileProDomain.workbench || {};
  window.AgileProDomain.workbench.normalizeToolKey = normalizeToolKey;
  window.AgileProDomain.workbench.getWorkbenchToolLabel = getWorkbenchToolLabel;
  window.AgileProDomain.workbench.buildWorkbenchPayload = buildWorkbenchPayload;
  window.AgileProDomain.workbench.buildWorkbenchPlaceholder = buildWorkbenchPlaceholder;
}
